import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';

import { getFirebaseDb } from './client';

export const LEVEL_DESIGN_IMAGE_ASSET_COLLECTION = 'level_design_image_assets';
export const SCENE_IMAGE_ASSET_COLLECTION = 'scene_image_assets';

const IMAGE_ASSET_CHUNK_COLLECTION = 'chunks';
const IMAGE_ASSET_CHUNK_SIZE = 200_000;

const chunkImageAsset = (value: string): string[] => {
  const chunks: string[] = [];
  for (let index = 0; index < value.length; index += IMAGE_ASSET_CHUNK_SIZE) {
    chunks.push(value.slice(index, index + IMAGE_ASSET_CHUNK_SIZE));
  }
  return chunks;
};

export interface ImageAssetDocumentMetadata {
  designId?: string;
  ownerId?: string;
  sceneId?: string;
  userId?: string;
}

export const persistImageAssetDocument = async (
  collectionName: string,
  assetId: string,
  imagePath: string,
  metadata: ImageAssetDocumentMetadata = {},
): Promise<void> => {
  const trimmedAssetId = assetId.trim();
  const trimmedImagePath = imagePath.trim();
  if (!trimmedAssetId || !trimmedImagePath) {
    return;
  }

  const db = getFirebaseDb();
  const assetRef = doc(db, collectionName, trimmedAssetId);
  const chunks = chunkImageAsset(trimmedImagePath);
  const batch = writeBatch(db);

  const existingDoc = await getDoc(assetRef).catch(() => null);
  if (existingDoc?.exists()) {
    const existingChunkCount = Number(existingDoc.data()?.chunkCount ?? 0);
    for (let i = chunks.length; i < existingChunkCount; i += 1) {
      batch.delete(doc(assetRef, IMAGE_ASSET_CHUNK_COLLECTION, String(i)));
    }
  }

  const metadataFields = Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) => typeof value === 'string' && value.trim().length > 0,
    ),
  );

  batch.set(assetRef, {
    assetId: trimmedAssetId,
    chunkCount: chunks.length,
    updatedAt: serverTimestamp(),
    ...metadataFields,
  });

  chunks.forEach((chunk, index) => {
    batch.set(doc(assetRef, IMAGE_ASSET_CHUNK_COLLECTION, String(index)), {
      data: chunk,
      index,
    });
  });

  await batch.commit();
};

export const readImageAssetDocument = async (
  collectionName: string,
  assetId: string,
): Promise<string | null> => {
  const trimmedAssetId = assetId.trim();
  if (!trimmedAssetId) {
    return null;
  }

  const db = getFirebaseDb();
  const assetRef = doc(db, collectionName, trimmedAssetId);
  let assetSnapshot;
  try {
    assetSnapshot = await getDoc(assetRef);
  } catch {
    return null;
  }
  if (!assetSnapshot.exists()) {
    return null;
  }

  const chunkCount = Number(assetSnapshot.data()?.chunkCount ?? 0);
  if (!Number.isInteger(chunkCount) || chunkCount <= 0) {
    return null;
  }

  let chunkSnapshots;
  try {
    chunkSnapshots = await getDocs(
      query(collection(assetRef, IMAGE_ASSET_CHUNK_COLLECTION), orderBy('index', 'asc')),
    );
  } catch {
    return null;
  }
  const chunks = new Array<string>(chunkCount).fill('');
  chunkSnapshots.docs.forEach((chunkSnapshot) => {
    const chunkData = chunkSnapshot.data() as Record<string, unknown>;
    const index = Number(chunkData.index);
    if (!Number.isInteger(index) || index < 0 || index >= chunkCount) {
      return;
    }
    chunks[index] = String(chunkData.data ?? '');
  });

  return chunks.every((chunk) => chunk.length > 0) ? chunks.join('') : null;
};

export const deleteImageAssetDocument = async (
  collectionName: string,
  assetId: string,
): Promise<void> => {
  const trimmedAssetId = assetId.trim();
  if (!trimmedAssetId) {
    return;
  }

  const db = getFirebaseDb();
  const assetRef = doc(db, collectionName, trimmedAssetId);
  const assetSnapshot = await getDoc(assetRef).catch(() => null);
  if (!assetSnapshot?.exists()) {
    return;
  }

  const chunkCount = Number(assetSnapshot.data()?.chunkCount ?? 0);
  const batch = writeBatch(db);

  for (let index = 0; index < chunkCount; index += 1) {
    batch.delete(doc(assetRef, IMAGE_ASSET_CHUNK_COLLECTION, String(index)));
  }
  batch.delete(assetRef);

  await batch.commit();
};

export const markImageAssetDocumentMigrated = async (
  collectionName: string,
  assetId: string,
  storagePath: string,
): Promise<void> => {
  const trimmedAssetId = assetId.trim();
  const trimmedStoragePath = storagePath.trim();
  if (!trimmedAssetId || !trimmedStoragePath) {
    return;
  }

  const db = getFirebaseDb();
  await setDoc(
    doc(db, collectionName, trimmedAssetId),
    {
      mediaStorageMigrationSource: 'save',
      mediaStorageMigratedAt: serverTimestamp(),
      mediaStoragePath: trimmedStoragePath,
      mediaStorageVersion: 'storage-v1',
    },
    { merge: true },
  );
};
